import type { Icon as TablerIcon } from '@tabler/icons-react';
import {
  IconCalendarEvent,
  IconBriefcase,
  IconHome,
  IconEngine,
  IconTool,
  IconShieldCheck,
  IconUsers,
  IconTruck,
  IconShieldLock,
  IconCoins,
  IconFileInvoice,
  IconReceipt2,
} from '@tabler/icons-react';
import { TRANSLATIONS } from './translations';

export interface IHostToolNavItem {
  key: string;
  /** The component, not an element: each caller renders it at its own size (20 on a phone sheet, 18 in a desktop dropdown). */
  Icon: TablerIcon;
  labelKey: string;
  path: string;
  /** Hidden until the user has a HostProfile -- there is nothing behind it before that. */
  hostOnly?: boolean;
  /**
   * Hidden until the user has a ServiceProviderProfile. Its own flag rather
   * than a second meaning for hostOnly: the two are separate modalities and
   * somebody can be either without being the other (see
   * ServiceProviderProfile's own doc comment). A plumber with no machines of
   * their own still needs their price list.
   */
  serviceProviderOnly?: boolean;
  /** Where a user without a HostProfile goes instead. Only on entries that are the way INTO being a host. */
  pathWithoutHostProfile?: string;
  /**
   * Route prefix that counts as "this entry is the screen on show". Only
   * needed when `path` points at one of several sibling routes -- Reservas
   * links to the guest tab but owns the incoming one too.
   */
  matchPrefix?: string;
  /**
   * Already a tab in the phone's bottom bar, so the "+" sheet must not repeat
   * it. Desktop has no such tab -- that is exactly why the entry is here.
   */
  inMobileTabBar?: boolean;
}

/**
 * Everything a host manages, in one list.
 *
 * There were two: the mobile bottom bar's "+" sheet and the desktop header's
 * nav menu, written separately and already drifted -- the phone had no way to
 * reach "Flota en vivo" or "Mi depósito" at all, because those were added to
 * the desktop list and nobody thought to add them to the other one.
 *
 * On desktop these are collapsed behind one icon in the header rather than
 * laid out across it: nine top-level links is a menu bar nobody reads. Only
 * Explorar stays out in the open as text; Reservas lives in here too, with
 * the unseen-bookings badge riding on the icon that opens it.
 */
export const HOST_TOOLS_NAV: IHostToolNavItem[] = [
  {
    key: 'bookings',
    Icon: IconCalendarEvent,
    labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_BOOKINGS_SHORT,
    path: '/heavy-duty-machinery/bookings/mine',
    // Incoming bookings is a tab inside this screen, not a destination of its
    // own, so the whole /bookings subtree lights this entry up.
    matchPrefix: '/heavy-duty-machinery/bookings',
    inMobileTabBar: true,
  },
  {
    key: 'quotes',
    Icon: IconFileInvoice,
    labelKey: TRANSLATIONS.SERVICE_REQUESTS_NAV_LABEL,
    path: '/heavy-duty-machinery/service-requests',
    // The whole /service-requests subtree, because the professional's side
    // is its own route (/incoming) and a single request is another -- all
    // three are this entry, the same way Reservas owns its incoming tab.
    matchPrefix: '/heavy-duty-machinery/service-requests',
    // Deliberately open to everybody: asking a professional for a price
    // needs no profile of any kind, and the customer's own list of what they
    // asked for had nowhere to be reached from at all.
  },
  {
    key: 'services',
    Icon: IconBriefcase,
    labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NAV_LABEL,
    path: '/heavy-duty-machinery/services',
    // Deliberately not hostOnly: a user can be a ServiceProvider without ever
    // being a Host (see ServiceProviderProfile's own doc comment).
  },
  {
    key: 'my-spaces',
    Icon: IconHome,
    labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_MY_SPACES,
    path: '/heavy-duty-machinery/mine',
    // The way into being a host: somebody who isn't one yet lands on the
    // onboarding wizard instead of an empty list.
    pathWithoutHostProfile: '/host/onboarding',
  },
  {
    key: 'my-rates',
    Icon: IconReceipt2,
    labelKey: TRANSLATIONS.MY_RATE_CATALOG_NAV_LABEL,
    path: '/heavy-duty-machinery/my-rates',
    // Where the price list and the assistant's standing questions live. Of
    // no use to somebody who does not sell a service, and until now of no use
    // to somebody who does either, because nothing linked to it.
    serviceProviderOnly: true,
  },
  { key: 'fleet', Icon: IconEngine, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_FLEET_NAV_LABEL, path: '/heavy-duty-machinery/fleet', hostOnly: true },
  { key: 'accessories', Icon: IconTool, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_NAV_LABEL, path: '/heavy-duty-machinery/accessories', hostOnly: true },
  { key: 'insurance-policies', Icon: IconShieldCheck, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICIES_NAV_LABEL, path: '/heavy-duty-machinery/insurance-policies', hostOnly: true },
  { key: 'operators', Icon: IconUsers, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_NAV_LABEL, path: '/heavy-duty-machinery/operators', hostOnly: true },
  { key: 'transport-vehicles', Icon: IconTruck, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLES_NAV_LABEL, path: '/heavy-duty-machinery/transport-vehicles', hostOnly: true },
  {
    key: 'host-earnings',
    Icon: IconCoins,
    labelKey: TRANSLATIONS.HOST_EARNINGS_NAV_LABEL,
    path: '/heavy-duty-machinery/host/earnings',
    // Deliberately not hostOnly, same as 'services' above: the earnings
    // endpoint pays out by BookingLine.RealProviderUserId, not by host, so a
    // service provider with no machines of their own has real earnings behind
    // this and simply could not reach them.
  },
  { key: 'host-deposit', Icon: IconShieldLock, labelKey: TRANSLATIONS.HOST_DEPOSIT_NAV_LABEL, path: '/heavy-duty-machinery/host/deposit', hostOnly: true },
];

/** Which of the two profiles this user has. Neither, either or both are all normal. */
export interface HostToolsAudience {
  hasHostProfile: boolean;
  hasServiceProviderProfile: boolean;
}

/**
 * The entries this user can actually reach.
 *
 * `surface` is the phone's "+" sheet or the desktop dropdown: the sheet drops
 * anything the bottom tab bar already shows, the dropdown keeps everything.
 *
 * An object rather than two booleans on purpose: the second one arrived with
 * the service-provider entries, and a call site that had them the wrong way
 * round would compile perfectly and hide the wrong rows.
 */
export const visibleHostTools = (
  audience: HostToolsAudience,
  surface: 'desktopMenu' | 'mobileSheet' = 'desktopMenu'
): IHostToolNavItem[] =>
  HOST_TOOLS_NAV.filter(
    (item) =>
      (!item.hostOnly || audience.hasHostProfile) &&
      (!item.serviceProviderOnly || audience.hasServiceProviderProfile) &&
      !(surface === 'mobileSheet' && item.inMobileTabBar)
  );

/** True when `pathname` is the screen this entry leads to. */
export const isHostToolActive = (item: IHostToolNavItem, pathname: string): boolean =>
  pathname.startsWith(item.matchPrefix ?? item.path);

/** Where an entry leads for this user. */
export const hostToolPath = (item: IHostToolNavItem, hasHostProfile: boolean): string =>
  !hasHostProfile && item.pathWithoutHostProfile ? item.pathWithoutHostProfile : item.path;
